import type {
  AttachmentIntentRequest,
  AttachmentIntentResponse,
  CompanyListResponse,
  ConversationCreateRequest,
  ConversationDetail,
  ConversationListItem,
  ConversationListResponse,
  Message,
  MessageCreateRequest,
  MessageListResponse,
  UnreadCountResponse
} from '@tandemone/types'

import { api } from '@/api/client'

/** super-only on the server. used by the new-conversation sheet's
 * recipient picker. surfaces a 403 for non-super callers (caught at the
 * mutation layer and surfaced inline).
 */
export async function listCompaniesForPicker(
  signal?: AbortSignal
): Promise<CompanyListResponse> {
  const response = await api.get<CompanyListResponse>('/v1/companies', {
    signal
  })
  return response.data
}

export async function listConversations(
  args: { search?: string } = {},
  signal?: AbortSignal
): Promise<ConversationListResponse> {
  const params: Record<string, string> = {}
  if (args.search && args.search.trim() !== '')
    params.search = args.search.trim()
  const response = await api.get<ConversationListResponse>(
    '/v1/conversations',
    { params, signal }
  )
  return response.data
}

export async function fetchUnreadCount(
  signal?: AbortSignal
): Promise<UnreadCountResponse> {
  const response = await api.get<UnreadCountResponse>(
    '/v1/conversations/unread-count',
    { signal }
  )
  return response.data
}

export async function fetchConversation(
  id: string,
  signal?: AbortSignal
): Promise<ConversationDetail> {
  const response = await api.get<ConversationDetail>(
    `/v1/conversations/${id}`,
    { signal }
  )
  return response.data
}

export async function listMessages(
  id: string,
  args: { before?: string; limit?: number } = {},
  signal?: AbortSignal
): Promise<MessageListResponse> {
  const params: Record<string, string> = {}
  if (args.before) params.before = args.before
  if (args.limit !== undefined) params.limit = String(args.limit)
  const response = await api.get<MessageListResponse>(
    `/v1/conversations/${id}/messages`,
    { params, signal }
  )
  return response.data
}

export async function createConversation(
  payload: ConversationCreateRequest
): Promise<ConversationListItem> {
  const response = await api.post<ConversationListItem>(
    '/v1/conversations',
    payload
  )
  return response.data
}

export async function sendMessage(
  conversationId: string,
  payload: MessageCreateRequest
): Promise<Message> {
  const response = await api.post<Message>(
    `/v1/conversations/${conversationId}/messages`,
    payload
  )
  return response.data
}

export async function markConversationRead(id: string): Promise<void> {
  await api.post(`/v1/conversations/${id}/read`)
}

export async function unsendMessage(
  conversationId: string,
  messageId: string
): Promise<void> {
  await api.delete(`/v1/conversations/${conversationId}/messages/${messageId}`)
}

/** attachment two-phase upload. callers:
 *   1. requestAttachmentUpload({ fileName, contentType, byteSize })
 *      → { attachmentId, uploadUrl, expiresAt }
 *   2. uploadAttachmentBytes(attachmentId, file). goes through our origin, not
 *      uploadUrl: corporate filters block the storage host while allowing ours.
 *   3. include attachmentId in the eventual send/create payload.
 */
export async function requestAttachmentUpload(
  payload: AttachmentIntentRequest
): Promise<AttachmentIntentResponse> {
  const response = await api.post<AttachmentIntentResponse>(
    '/v1/conversations/attachments/intent',
    payload
  )
  return response.data
}

export async function uploadAttachmentBytes(
  attachmentId: string,
  file: File
): Promise<void> {
  await api.put(`/v1/conversations/attachments/${attachmentId}/content`, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' }
  })
}
